import { AlertColor, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { createDropdownData, getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { AddEditEmailEventConfigForm } from './AddEditEmailEventConfiguration/AddEditEmailEventConfigurationForm';

const asList = (value: any) => {
  if (Array.isArray(value)) return value;
  if (value === '' || value === undefined || value === null) return [];
  return String(value)
    .split(',')
    .map((item: string) => item.trim())
    .filter(Boolean);
};

const asRoleIds = (value: any) => {
  const list = Array.isArray(value) ? value : [];
  return list.map((item: any) => parseInt(item.value, 10));
};

const EmailEventConfigurationList: FC = () => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = screenSizeDownSm ? '100%' : screenSizeDownMd ? '85%' : '55%';
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [eventTypes, setEventTypes] = useState<{ label: string; value: any }[]>([]);
  const [mailTypes, setMailTypes] = useState<{ label: string; value: any }[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(
    () => AddEditEmailEventConfigForm(strings, eventTypes as unknown as [], mailTypes as unknown as [], editingId),
    [eventTypes, mailTypes, editingId],
  );

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.emailEventConfig, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(URLS.emailEventConfigDropdowns);
        setEventTypes((data.eventTypes || []).map((item: any) => ({ label: item.name, value: item.id })));
        setMailTypes((data.mailTemplates || []).map((item: any) => ({ label: item.name, value: item.name })));
        setRoles(data.roles || []);
      } catch (error: any) {
        // ignore dropdown load failures
      }
    })();
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const emptyFormValues: FormDataModel = {
    eventType: '',
    mailType: '',
    toRoleIds: [],
    ccRoleIds: [],
    toEmailAddresses: '',
    ccEmailAddresses: '',
    adminOnly: false,
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.emailEventConfigById(recordId));
          const toRoleIdsSelected = roles.filter((item: any) => data.toRoleIds?.split(',').includes(item.id.toString()));
          const ccRoleIdsSelected = roles.filter((item: any) => data.ccRoleIds?.split(',').includes(item.id.toString()));
          setFormValues({
            eventType: data?.eventType?.value ?? '',
            mailType: data?.mailType?.value ?? '',
            toRoleIds: createDropdownData(toRoleIdsSelected, ['id', 'name']),
            ccRoleIds: createDropdownData(ccRoleIdsSelected, ['id', 'name']),
            toEmailAddresses: data?.toEmailAddresses ?? '',
            ccEmailAddresses: data?.ccEmailAddresses ?? '',
            adminOnly: data?.adminOnly ?? false,
          });
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        setFormValues(emptyFormValues);
      }
      setDrawerOpen(true);
    },
    [roles],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = {
        ...body,
        toEmailAddresses: asList(body.toEmailAddresses),
        ccEmailAddresses: asList(body.ccEmailAddresses),
        toRoleIds: asRoleIds(body.toRoleIds),
        ccRoleIds: asRoleIds(body.ccRoleIds),
      };
      if (editingId) {
        await axiosInstance.put(URLS.emailEventConfigById(editingId), payload);
      } else {
        await axiosInstance.post(URLS.emailEventConfigAdd, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.emailEventConfigById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.menu_text_email_event_config,
      subtitle: strings.email_event_config_list_subtitle,
      searchField: 'eventType',
      searchPlaceholder: 'Search email event triggers',
      AddButtonText: strings.email_event_config_title_add,
      listApi: URLS.emailEventConfig,
      deleteApi: URLS.emailEventConfigById,
      addPrivilege: privileges.add_email_event_config,
      editPrivilege: privileges.edit_email_event_config,
      deletePrivilege: privileges.delete_email_event_config,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        { field: 'eventType', headerName: strings.email_event_config_column_event_type, flex: 3, sortable: true },
        { field: 'mailType', headerName: strings.email_event_config_column_mail_type, flex: 3, sortable: true },
        { field: 'mailSubject', headerName: strings.mailTemplate_text_subject, flex: 4, sortable: false },
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.email_event_config_title_edit : strings.email_event_config_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_email_event_config)}
        width={drawerWidth}
        deleteEntityName={
          (formValues.eventType &&
            eventTypes.find(item => item.value === formValues.eventType)?.label) ||
          undefined
        }
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default EmailEventConfigurationList;
